import { useLayoutEffect, useRef, type ButtonHTMLAttributes, type Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Icon } from './Icon';
import type { IconName } from './icons';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'md' | 'lg' | 'sm';
  icon?: IconName;
  busy?: boolean;
  block?: boolean;
}

const btnClass = (variant: Variant = 'secondary', size: string = 'md', block?: boolean, extra = '') =>
  ['btn', `btn-${variant}`, size !== 'md' ? `btn-${size}` : '', block ? 'btn-block' : '', extra].filter(Boolean).join(' ');

/**
 * Button with a busy state: disabled while an async action runs (no double submit), its content kept in place but
 * hidden under a centred spinner, and its width held from before (no layout shift, even when the label changes).
 * Press feedback and the focus ring come from button.css.
 */
export function Button({ variant, size, icon, busy, block, className, children, disabled, type = 'button', style, ref, ...rest }: BtnProps & { ref?: Ref<HTMLButtonElement> }) {
  const own = useRef<HTMLButtonElement | null>(null);
  const width = useRef(0);
  useLayoutEffect(() => {
    if (!busy && own.current) width.current = own.current.offsetWidth;
  });
  const setRef = (el: HTMLButtonElement | null) => {
    own.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };
  return (
    <button
      ref={setRef}
      type={type}
      className={btnClass(variant, size, block, className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      style={busy && width.current ? { minWidth: width.current, ...style } : style}
      {...rest}
    >
      {busy && <span className="spinner btn-spinner" aria-hidden="true" />}
      {icon && <Icon name={icon} size={size === 'lg' ? 20 : 18} />}
      {children}
    </button>
  );
}

export function ButtonLink({ variant, size, icon, block, className, children, ...rest }: LinkProps & { variant?: Variant; size?: 'md' | 'lg' | 'sm'; icon?: IconName; block?: boolean }) {
  return (
    <Link className={btnClass(variant, size, block, className)} {...rest}>
      {icon && <Icon name={icon} size={size === 'lg' ? 20 : 18} />}
      {children}
    </Link>
  );
}
